import type {
  AccessGrantEffect,
  AccessScope,
  AdminUserAccessOverride,
} from './accessRequests'

const norm = (c: string) => String(c || '').trim().toUpperCase()

export type OverridePresetId =
  | 'READ_ONLY'
  | 'FULL_MUTATIONS_ALLOWED'
  | 'NO_CREATE'
  | 'NO_EDIT'
  | 'NO_DELETE'
  | 'NO_APPROVE'
  | 'READ_NO_DOWNLOAD'
  | 'READ_AND_CREATE_ONLY'

export type OverrideModuleId = 'admin_surface' | 'operations_cargo' | 'procurement_ops'

export type OverrideFeatureId = string

export type OverrideRecipe = {
  id: string
  label: string
  description: string
  moduleId: OverrideModuleId
  featureId: OverrideFeatureId
  presetId: OverridePresetId
}

/** Feature = subset of permission codes within a module (predicate on uppercase code). */
export type OverrideFeatureDef = {
  id: OverrideFeatureId
  label: string
  /** Further filter within module-matched registry codes */
  matches: (codeUpper: string) => boolean
}

export type OverrideModuleDef = {
  id: OverrideModuleId
  label: string
  /** Broad grouping: which registry strings belong to this module */
  matches: (codeUpper: string) => boolean
  features: OverrideFeatureDef[]
}

const always: OverrideFeatureDef = {
  id: 'ALL',
  label: 'Entire module',
  matches: () => true,
}

/** Admin UI / tenant admin permissions from access bundles (see DB seed). */
export const OVERRIDE_MODULES: OverrideModuleDef[] = [
  {
    id: 'admin_surface',
    label: 'Admin — companies, vessels, users, roles',
    matches: (c) =>
      /^(READ|CREATE|UPDATE|DELETE)_(VESSEL|COMPANY_ADMIN|COMPANY_GROUP_ADMIN|OPERATOR|SHORE|ROLE|PERMISSION)\b/.test(
        c
      ) ||
      /^READ_USER\b/.test(c) ||
      /\bROLE_PERMISSION\b/.test(c),
    features: [
      always,
      {
        id: 'VESSELS',
        label: 'Vessels',
        matches: (c) => /\bVESSEL\b/.test(c),
      },
      {
        id: 'COMPANY_CGA',
        label: 'Company & company-group admins',
        matches: (c) => /COMPANY_ADMIN|COMPANY_GROUP_ADMIN/.test(c),
      },
      {
        id: 'USER_DIRECTORY',
        label: 'User directory',
        matches: (c) => /\bUSER\b/.test(c) && !/USER_PERMISSION/.test(c),
      },
      {
        id: 'TENANT_USERS',
        label: 'Operator & shore users',
        matches: (c) => /\bOPERATOR\b|\bSHORE\b/.test(c),
      },
      {
        id: 'RBAC',
        label: 'Roles & permissions (RBAC)',
        matches: (c) => /\bROLE\b|\bPERMISSION\b/.test(c),
      },
    ],
  },
  {
    id: 'operations_cargo',
    label: 'Operations — cargo operations',
    matches: (c) => /CARGO_OPERATION/.test(c),
    features: [
      always,
      {
        id: 'CARGO_OPERATIONS',
        label: 'Cargo operations screens',
        matches: (c) => /CARGO_OPERATION/.test(c),
      },
    ],
  },
  {
    id: 'procurement_ops',
    label: 'Procurement (requisitions, PO, invoices, …)',
    matches: (c) =>
      /^(REQUISITION|RFQ|QUOTATION|GRN|INVOICE|DELIVERY|PROCUREMENT_LOG|USER_PERMISSION)_/.test(c) ||
      /^PO_/.test(c) ||
      /^LIBRARY_/.test(c),
    features: [
      always,
      {
        id: 'INVOICE_MGMT',
        label: 'Invoice management',
        matches: (c) => /^INVOICE_/.test(c),
      },
      {
        id: 'REQUISITIONS',
        label: 'Requisitions',
        matches: (c) => /^REQUISITION_/.test(c),
      },
      {
        id: 'PURCHASE_ORDERS',
        label: 'Purchase orders',
        matches: (c) => /^PO_/.test(c),
      },
      {
        id: 'RFQ_QUOTE',
        label: 'RFQ & quotations',
        matches: (c) => /^RFQ_|^QUOTATION_/.test(c),
      },
      {
        id: 'GOODS_RECEIPT',
        label: 'Goods receipt (GRN)',
        matches: (c) => /^GRN_/.test(c),
      },
      {
        id: 'LIBRARY',
        label: 'Procurement libraries / inventory masters',
        matches: (c) => /^LIBRARY_/.test(c),
      },
    ],
  },
]

export const OVERRIDE_PRESETS: {
  id: OverridePresetId
  label: string
  description: string
}[] = [
  {
    id: 'READ_ONLY',
    label: 'Read only',
    description:
      'Deny changes: create, edit, delete, submit, send, approve, manage, and other non-view actions in the chosen area.',
  },
  {
    id: 'NO_CREATE',
    label: 'No create',
    description: 'Deny create-style actions (CREATE_*, …_CREATE, REQUISITION_CREATE, etc.).',
  },
  {
    id: 'NO_EDIT',
    label: 'No edit / update',
    description: 'Deny update/edit (UPDATE_*, …_EDIT).',
  },
  {
    id: 'NO_DELETE',
    label: 'No delete',
    description: 'Deny delete (DELETE_*, …_DELETE).',
  },
  {
    id: 'NO_APPROVE',
    label: 'No approve / reject',
    description: 'Deny permissions whose code suggests approval or reject flows.',
  },
  {
    id: 'READ_NO_DOWNLOAD',
    label: 'Read only + block download/export',
    description: 'Same as read only, plus deny codes that look like download or export.',
  },
  {
    id: 'READ_AND_CREATE_ONLY',
    label: 'Read + create only',
    description:
      'Allow viewing and creating only; deny update, delete, approvals, submit, send, and manage-style actions.',
  },
  {
    id: 'FULL_MUTATIONS_ALLOWED',
    label: 'Allow mutations (unusual)',
    description:
      'Adds ALLOW overrides for mutation-style codes in the area. Use rarely; packages usually grant access.',
  },
]

/** One-click combinations for the UX spec. */
export const OVERRIDE_RECIPES: OverrideRecipe[] = [
  {
    id: 'admin_readonly',
    label: 'Admin — read only',
    description: 'Typical “see admin screens but cannot change anything.”',
    moduleId: 'admin_surface',
    featureId: 'ALL',
    presetId: 'READ_ONLY',
  },
  {
    id: 'ops_cargo_readonly',
    label: 'Cargo operations — read only',
    description: 'View cargo operations only.',
    moduleId: 'operations_cargo',
    featureId: 'ALL',
    presetId: 'READ_ONLY',
  },
  {
    id: 'vessel_no_delete',
    label: 'Vessels — no delete',
    description: 'Keep vessel access but block DELETE_VESSEL (and similar).',
    moduleId: 'admin_surface',
    featureId: 'VESSELS',
    presetId: 'NO_DELETE',
  },
  {
    id: 'cargo_read_create',
    label: 'Cargo — read + create only',
    description: 'Create new cargo ops but not update/delete/approve-style actions.',
    moduleId: 'operations_cargo',
    featureId: 'CARGO_OPERATIONS',
    presetId: 'READ_AND_CREATE_ONLY',
  },
  {
    id: 'invoice_no_mark_paid',
    label: 'Invoices — no mark paid / payment updates',
    description:
      'UI uses INVOICE_CREATE for marking paid; this denies that permission in invoice features.',
    moduleId: 'procurement_ops',
    featureId: 'INVOICE_MGMT',
    presetId: 'NO_CREATE',
  },
]

function isReadLike(code: string): boolean {
  const c = norm(code)
  if (!c) return false
  if (c.startsWith('READ_')) return true
  if (c.endsWith('_VIEW')) return true
  return false
}

function isCreateLike(code: string): boolean {
  const c = norm(code)
  if (!c) return false
  if (c.startsWith('CREATE_')) return true
  if (c.includes('_CREATE')) return true
  return false
}

function isUpdateLike(code: string): boolean {
  const c = norm(code)
  if (!c) return false
  if (c.startsWith('UPDATE_')) return true
  if (c.includes('_EDIT')) return true
  return false
}

function isDeleteLike(code: string): boolean {
  const c = norm(code)
  if (!c) return false
  if (c.startsWith('DELETE_')) return true
  if (c.includes('_DELETE')) return true
  return false
}

function isApproveLike(code: string): boolean {
  const c = norm(code)
  return c.includes('APPROVE') || c.includes('_REJECT') || /_REJECT\b/.test(c)
}

function isDownloadLike(code: string): boolean {
  const c = norm(code)
  return c.includes('DOWNLOAD') || c.includes('EXPORT')
}

function isManageLike(code: string): boolean {
  const c = norm(code)
  return c.includes('_MANAGE') || c.endsWith('_MANAGE')
}

function isSubmitSendLike(code: string): boolean {
  const c = norm(code)
  return c.includes('_SUBMIT') || c.includes('_SEND') || c.endsWith('_SUBMIT') || c.endsWith('_SEND')
}

/** Non–read-like actions we typically deny under “read only”. */
function isNonReadMutation(code: string): boolean {
  if (isReadLike(code)) return false
  return (
    isCreateLike(code) ||
    isUpdateLike(code) ||
    isDeleteLike(code) ||
    isApproveLike(code) ||
    isManageLike(code) ||
    isSubmitSendLike(code) ||
    /\bADD_ITEMS\b/.test(norm(code)) ||
    /\bCOMPARE\b|\bSELECT\b/.test(norm(code))
  )
}

function shouldDenyForPreset(code: string, preset: OverridePresetId): boolean {
  switch (preset) {
    case 'READ_ONLY':
      return isNonReadMutation(code)
    case 'READ_NO_DOWNLOAD':
      return isNonReadMutation(code) || isDownloadLike(code)
    case 'NO_CREATE':
      return isCreateLike(code)
    case 'NO_EDIT':
      return isUpdateLike(code)
    case 'NO_DELETE':
      return isDeleteLike(code)
    case 'NO_APPROVE':
      return isApproveLike(code)
    case 'READ_AND_CREATE_ONLY':
      return !isReadLike(code) && !isCreateLike(code)
    case 'FULL_MUTATIONS_ALLOWED':
      return false
    default:
      return false
  }
}

function shouldAllowForPreset(code: string, preset: OverridePresetId): boolean {
  if (preset !== 'FULL_MUTATIONS_ALLOWED') return false
  return isNonReadMutation(code)
}

export type SimpleOverrideBuildResult = {
  /** Rows to merge into override list (only codes in the permission registry). */
  overrides: AdminUserAccessOverride[]
  /** Human-readable lines for preview */
  previewLines: string[]
  /** Codes we would have touched but are not in GET /admin/access/permissions */
  notInRegistry: string[]
  /** All codes considered in scope before registry filter */
  scopeCodes: string[]
}

export function getModuleDef(moduleId: OverrideModuleId): OverrideModuleDef | undefined {
  return OVERRIDE_MODULES.find((m) => m.id === moduleId)
}

export function filterCodesInScope(
  registryUpper: string[],
  moduleId: OverrideModuleId,
  featureId: OverrideFeatureId
): string[] {
  const mod = getModuleDef(moduleId)
  if (!mod) return []
  const feat = mod.features.find((f) => f.id === featureId) ?? always
  return registryUpper.filter((c) => mod.matches(c) && feat.matches(c))
}

function expectedCodesMissingFromRegistry(
  moduleId: OverrideModuleId,
  featureId: OverrideFeatureId,
  presetId: OverridePresetId,
  registrySet: Set<string>
): string[] {
  const miss: string[] = []
  if (
    moduleId === 'procurement_ops' &&
    featureId === 'INVOICE_MGMT' &&
    (presetId === 'NO_CREATE' || presetId === 'READ_ONLY' || presetId === 'READ_AND_CREATE_ONLY')
  ) {
    if (!registrySet.has('INVOICE_CREATE')) miss.push('INVOICE_CREATE')
  }
  if (
    moduleId === 'operations_cargo' &&
    (featureId === 'ALL' || featureId === 'CARGO_OPERATIONS') &&
    (presetId === 'READ_ONLY' || presetId === 'NO_DELETE')
  ) {
    ;['DELETE_CARGO_OPERATION', 'UPDATE_CARGO_OPERATION', 'CREATE_CARGO_OPERATION'].forEach((c) => {
      if (!registrySet.has(c)) miss.push(c)
    })
  }
  return Array.from(new Set(miss))
}

/**
 * Build override rows from module + feature + preset. Only codes present in
 * {@code permissionRegistry} are emitted; missing “expected” codes appear in {@code notInRegistry}.
 */
export function buildSimpleOverrides(
  permissionRegistry: string[],
  moduleId: OverrideModuleId,
  featureId: OverrideFeatureId,
  presetId: OverridePresetId,
  opts: { reason?: string | null; scope?: AccessScope | null }
): SimpleOverrideBuildResult {
  const registrySet = new Set(permissionRegistry.map(norm).filter(Boolean))
  const mod = getModuleDef(moduleId)
  const previewLines: string[] = []
  const overrides: AdminUserAccessOverride[] = []

  if (!mod) {
    return { overrides: [], previewLines: ['Unknown module.'], notInRegistry: [], scopeCodes: [] }
  }
  const feat = mod.features.find((f) => f.id === featureId) ?? always

  const allKnown = Array.from(registrySet)
  const scopeCodes = allKnown.filter((c) => mod.matches(c) && feat.matches(c))

  const reasonSuffix = opts.reason?.trim() || null
  const defaultReason =
    reasonSuffix ||
    (presetId === 'READ_ONLY'
      ? 'Simple builder: read only'
      : `Simple builder: ${OVERRIDE_PRESETS.find((p) => p.id === presetId)?.label || presetId}`)

  for (const code of scopeCodes) {
    const deny = shouldDenyForPreset(code, presetId)
    const allow = shouldAllowForPreset(code, presetId)
    if (!deny && !allow) continue
    const effect: AccessGrantEffect = allow ? 'ALLOW' : 'DENY'
    const line = `${effect} ${code}${reasonSuffix ? ` — ${reasonSuffix}` : ''}`

    overrides.push({
      permissionCode: code,
      effect,
      scope: opts.scope ?? null,
      reason: defaultReason,
    })
    previewLines.push(line)
  }

  const notInRegistry = expectedCodesMissingFromRegistry(moduleId, featureId, presetId, registrySet)
  if (notInRegistry.length > 0) {
    previewLines.unshift(
      `Note: expected permission code(s) not in the current catalog: ${notInRegistry.join(', ')}. They were not added. Use Advanced mode if your environment still relies on them.`
    )
  }

  if (scopeCodes.length === 0 && previewLines.length <= 1) {
    previewLines.push(
      'No permissions in the system registry matched this module/area. Try Advanced mode with a raw code, or widen the module once those permissions exist in the catalog.'
    )
  }

  return { overrides, previewLines, notInRegistry, scopeCodes }
}

export function dedupeOverridesByPermission(
  rows: AdminUserAccessOverride[]
): AdminUserAccessOverride[] {
  const map = new Map<string, AdminUserAccessOverride>()
  for (const row of rows) {
    const k = norm(row.permissionCode)
    if (!k) continue
    map.set(k, {...row, permissionCode: k})
  }
  return Array.from(map.values())
}

export function mergeOverrideLists(
  existing: AdminUserAccessOverride[],
  additions: AdminUserAccessOverride[]
): AdminUserAccessOverride[] {
  return dedupeOverridesByPermission([...existing, ...additions])
}
